import { useCallback, useEffect, useRef, useState } from 'react';

import { GlobalOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';

/**
 * Reloj y zona horaria de la sesión, en el navbar de toda la aplicación.
 *
 * Está acá y no dentro de un módulo porque la regla es transversal: todas las
 * horas de la aplicación se muestran en la zona del usuario, así que el dato que
 * lo explica se dice una sola vez y arriba.
 *
 * Muestra `HH:mm` y el offset; el nombre completo de la zona va en el hover,
 * que es información que se consulta una vez.
 */
function TimeZoneBadge() {
  const { t, i18n } = useTranslation();

  const [now, setNow] = useState(() => new Date());
  // Con el detalle abierto se muestran segundos, así que hay que latir más
  // rápido; cerrado alcanza con una vez por minuto.
  const [open, setOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  /**
   * Cada tick RECALCULA desde el reloj del sistema en vez de sumarle tiempo al
   * valor anterior. Con la pestaña de fondo el navegador estrangula los timers
   * —Chrome los baja a uno por minuto y puede congelarlos—, así que un contador
   * acumulado se atrasaría para siempre; recalculando, un tick tardío igual
   * pinta la hora correcta.
   *
   * Se reprograma al borde de la siguiente unidad visible en vez de a intervalo
   * fijo: sin deriva y sin repintar más veces de las que cambia lo que se ve.
   */
  const tick = useCallback((withSeconds: boolean) => {
    clearTimeout(timer.current);
    setNow(new Date());

    const step = withSeconds ? 1_000 : 60_000;
    const msToNextStep = step - (Date.now() % step);
    timer.current = setTimeout(() => tick(withSeconds), msToNextStep + 20);
  }, []);

  useEffect(() => {
    tick(open);
    return () => clearTimeout(timer.current);
  }, [tick, open]);

  useEffect(() => {
    // Volver a la pestaña es el momento en que la hora puede estar más vieja:
    // el timer pudo quedar estrangulado o congelado mientras estaba de fondo.
    const resync = () => {
      if (document.visibilityState === 'visible') tick(open);
    };

    document.addEventListener('visibilitychange', resync);
    window.addEventListener('focus', resync);

    return () => {
      document.removeEventListener('visibilitychange', resync);
      window.removeEventListener('focus', resync);
    };
  }, [tick, open]);

  let timeZone = 'UTC';
  try {
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    timeZone = 'UTC';
  }

  const time = new Intl.DateTimeFormat(i18n.language, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone,
  }).format(now);

  /**
   * En el badge la hora va sin segundos —cambiar sesenta veces por minuto en el
   * navbar es ruido—, pero eso lo hace parecer detenido. El detalle sí los
   * muestra: es la prueba a la vista de que el reloj está corriendo.
   */
  const timeWithSeconds = new Intl.DateTimeFormat(i18n.language, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone,
  }).format(now);

  // `shortOffset` da "GMT-5"; se muestra como UTC, que es lo que se lee mejor.
  let offset = '';
  try {
    const parts = new Intl.DateTimeFormat('en', {
      timeZone,
      timeZoneName: 'shortOffset',
    }).format(now);
    // Los minutos son parte del offset y no un adorno: hay zonas a media hora e
    // incluso a 45 minutos —India es UTC+5:30 y Nepal UTC+5:45—, así que
    // quedarse solo con las horas mostraría una zona que no es la del usuario.
    offset = (parts.match(/GMT[+-]?\d{1,2}(?::\d{2})?/) || ['GMT'])[0].replace(
      'GMT',
      'UTC'
    );
  } catch {
    offset = '';
  }

  return (
    <Tooltip
      // Hover no alcanza: en táctil no existe y por teclado el badge es
      // enfocable, así que el detalle tiene que abrirse también ahí.
      trigger={['hover', 'focus', 'click']}
      open={open}
      onOpenChange={setOpen}
      title={
        <span className="timezone-badge__tip">
          <b className="timezone-badge__tip-time">{timeWithSeconds}</b>
          <span>
            {timeZone} · {offset}
          </span>
          <span>{t('common.timezone_badge.hint')}</span>
        </span>
      }
    >
      {/* Enfocable a propósito: en escritorio el detalle se ve al pasar el
          mouse, pero en táctil y por teclado hace falta poder alcanzarlo. */}
      <span className="timezone-badge" tabIndex={0}>
        <GlobalOutlined />
        <b>{time}</b>
        <span className="timezone-badge__offset">{offset}</span>
      </span>
    </Tooltip>
  );
}

export default TimeZoneBadge;
